// colorParser.ts
import chroma from 'chroma-js';

class ColorParser {
  private colorMap = new Map<string, string>();
  private initialized = false;
  
  constructor() {
    this.initializeColorMap();
  }
  
  private initializeColorMap() {
    if (this.initialized) return;
    
    const customColors: Record<string, string> = {
      'rose gold': '#E8B4B8',
      'nude': '#E3BC9A',
      'sand': '#C2B280',
      'golden': '#FFD700',
      'champagne': '#F7E7CE',
      'pearl': '#EAE0C8',
      'platinum': '#E5E4E2',
      'copper': '#B87333',
      'bronze': '#CD7F32',
      'gunmetal': '#2C3539',
      'charcoal': '#36454F',
      'steel blue': '#4682B4',
      'forest green': '#228B22',
      'burgundy': '#800020',
      'mauve': '#E0B0FF',
      'taupe': '#483C32',
      'ecru': '#C2B280',
      'ivory': '#FFFFF0',
      'cream': '#FFFDD0',
      'off white': '#FAF0E6',
      'warm gray': '#808069',
      'cool gray': '#8C92AC',
      'dusty rose': '#DCAE96',
      'sage': '#9CAF88',
      'sage green': '#9CAF88',
      'terracotta': '#E2725B',
      'clay': '#B66A50',
      'rust': '#B7410E',
      'ochre': '#CC7722',
      'umber': '#635147',
      'sienna': '#A0522D',
      'burnt orange': '#CC5500',
      'burnt sienna': '#E97451',
      'raw umber': '#826644',
      'sepia': '#704214',
      'mahogany': '#C04000',
      'walnut': '#773F1A',
      'chestnut': '#954535',
      'caramel': '#FFD59A',
      'honey': '#FFC30B',
      'amber': '#FFBF00',
      'mustard': '#FFDB58',
      'olive drab': '#6B8E23',
      'sage brush': '#9C9F84',
      'mint': '#3EB489',
      'seafoam': '#71EEB8',
      'teal blue': '#367588',
      'peacock': '#005F69',
      'navy blue': '#000080',
      'midnight blue': '#191970',
      'royal blue': '#4169E1',
      'cobalt': '#0047AB',
      'cerulean': '#007BA7',
      'azure': '#007FFF',
      'periwinkle': '#CCCCFF',
      'lavender gray': '#C4C3D0',
      'mauve taupe': '#915F6D',
      'dusty purple': '#825D78',
      'plum': '#8E4585',
      'eggplant': '#614051',
      'mulberry': '#C54B8C',
      'fuchsia': '#FF00FF',
      'hot pink': '#FF69B4',
      'bubblegum': '#FFC1CC',
      'cotton candy': '#FFBCD9',
      'blush': '#FFC0CB',
      'coral pink': '#F88379',
      'salmon pink': '#FF91A4',
      'peach': '#FFCBA4',
      'apricot': '#FBCEB1',
      'papaya': '#FFEFD5',
      'cantaloupe': '#FFA366',
      'tangerine': '#F28500',
      'sunset orange': '#FF8C69',
      'pumpkin': '#FF7518',
      'persimmon': '#EC5800',
      'cardinal': '#C41E3A',
      'crimson red': '#DC143C',
      'cherry': '#DE3163',
      'berry': '#8F021C',
      'wine': '#722F37',
      'bordeaux': '#6D071A',
      'maroon red': '#800000',
      // WMS/Industrial colors
      'safety orange': '#FF6600',
      'safety yellow': '#FFD300',
      'safety green': '#00A651',
      'safety red': '#C8102E',
      'warning amber': '#FFBF00',
      'caution yellow': '#FFD300',
      'danger red': '#C8102E',
      'go green': '#00A651',
      'stop red': '#C8102E',
      'alert orange': '#FF6600',
      'high vis yellow': '#FFD300',
      'high vis orange': '#FF6600',
      'high vis green': '#00A651',
      'reflective white': '#FFFFFF',
      'reflective silver': '#C0C0C0',
      'warehouse blue': '#1565C0',
      'forklift orange': '#FF6600',
      'pallet brown': '#8B4513',
      'steel gray': '#71797E',
      'concrete gray': '#A8A8A8',
      'machinery yellow': '#FFD300',
      'pipe blue': '#0066CC',
      'electrical orange': '#FF6600',
      'gas yellow': '#FFFF00',
      'water blue': '#0099CC',
    };
    
    Object.entries(customColors).forEach(([name, hex]) => {
      this.addColorVariations(name, hex);
    });
    
    this.initialized = true;
  }
  
  private addColorVariations(name: string, hex: string) {
    const variations = [
      name.toLowerCase(),
      name.toLowerCase().replace(/\s+/g, ''),
      name.toLowerCase().replace(/\s+/g, '-'),
      name.toLowerCase().replace(/\s+/g, '_'),
      name.toLowerCase().replace(/-/g, ''),
      name.toLowerCase().replace(/_/g, ''),
      name.toLowerCase().replace(/'/g, ''),
      name.toLowerCase().replace(/"/g, ''),
    ];
    
    variations.forEach(variation => {
      if (variation && !this.colorMap.has(variation)) {
        this.colorMap.set(variation, hex);
      }
    });
  }
  
  parse(colorValue: string): string {
    if (!colorValue || typeof colorValue !== 'string') return '#000000';
    
    const trimmed = colorValue.trim();
    if (!trimmed) return '#000000';
    
    try {
      return chroma(trimmed).hex();
    } catch (error) {
      const normalized = trimmed.toLowerCase()
        .replace(/\s+/g, '')
        .replace(/-/g, '')
        .replace(/_/g, '')
        .replace(/'/g, '')
        .replace(/"/g, '');
      
      if (this.colorMap.has(normalized)) {
        return this.colorMap.get(normalized)!;
      }
      
      const fuzzyResult = this.fuzzyMatch(normalized);
      if (fuzzyResult) {
        return fuzzyResult;
      }
      
      if (typeof window !== 'undefined' && document) {
        try {
          const div = document.createElement('div');
          div.style.color = trimmed;
          document.body.appendChild(div);
          
          const computedColor = window.getComputedStyle(div).color;
          document.body.removeChild(div);
          
          if (computedColor && computedColor !== 'rgba(0, 0, 0, 0)') {
            if (computedColor.startsWith('rgb')) {
              return this.rgbToHex(computedColor);
            }
            return computedColor;
          }
        } catch (browserError) {
          console.warn('Browser color parsing failed:', browserError);
        }
      }
    }
    
    console.warn('Color not found:', colorValue);
    return '#000000';
  }
  
  private fuzzyMatch(input: string): string | null {
    if (!input) return null;
    
    for (const [name, hex] of this.colorMap.entries()) {
      if (name.includes(input) || input.includes(name)) {
        return hex;
      }
    }
    
    const cleanInput = input.replace(/(light|dark|deep|pale|bright|medium|soft|hard|pure|true|real)/g, '');
    if (cleanInput !== input && cleanInput.length > 2) {
      for (const [name, hex] of this.colorMap.entries()) {
        if (name.includes(cleanInput) || cleanInput.includes(name)) {
          return hex;
        }
      }
    }
    
    return null;
  }
  
  private rgbToHex(rgb: string): string {
    const result = rgb.match(/\d+/g);
    if (result && result.length >= 3) {
      const [r, g, b] = result.map(Number);
      return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
    }
    return rgb;
  }
}

const colorParserInstance = new ColorParser();

export const colorParser = (colorValue: string): string => {
  return colorParserInstance.parse(colorValue);
};